import React from 'react';
import { Input, Button } from '@lomadee/design-system';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import {
  HUNTER_FUN_HOST_NAME,
  IROBOT_HOST_NAME,
  MARI_MARIA_MAKEUP_HOST_NAME,
  MARY_KAY_WHITELABEL,
  NUTRIBULLET_HOST_NAME,
  OLIST_HOST_NAME,
} from '@plug/config/consts';
import { Flex } from '@plug/ui2/rebass';
import { Spacer } from '@plug/ui';
import MaskedInput from '@plug/components/masked-input';
import { getHublyFormUnmaskedValue } from '@plug/helpers/hubly-form';
import { useCreateAccountContext } from '@plug/contexts/CreateAccountContext';
import AffiliateRegistrationCustomFields from '../AffiliateRegistrationCustomFields';
import {
  AffiliateRegisterPersonalDataSchemaCNPJ,
  AffiliateRegisterPersonalDataSchemaCPF,
  AffiliateRegisterPersonalDataSchemaWithCustomFields,
} from './AffiliateRegistrationPersonalData.schema';
import { TitleForm } from '../../style';
import gql from 'graphql-tag';
import { useApolloClient } from '@apollo/client';

const AffiliateRegistrationPersonalDataStep: React.FC = () => {
  const client = useApolloClient();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const isOlist = OLIST_HOST_NAME.includes(window.location.hostname);

  const riqueredPhone = [
    ...OLIST_HOST_NAME,
    ...NUTRIBULLET_HOST_NAME,
    ...IROBOT_HOST_NAME,
    ...HUNTER_FUN_HOST_NAME,
  ].includes(window.location.hostname);

  const onlyCNPJ = [...OLIST_HOST_NAME, ...MARI_MARIA_MAKEUP_HOST_NAME].includes(
    window.location.hostname,
  );

  const {
    values: contextValues,
    setStep,
    addValues,
    whitelabelCustomization,
    role,
    isFieldBlocked,
  } = useCreateAccountContext();
  const { t } = useTranslation();

  const handleSubmitFormik = (formikValues: CreateAccountFormI): void => {
    addValues(formikValues);
    setStep('BANK_INFORMATION');
  };

  const handleBackToPasswordStep = (): void => {
    setStep('PASSWORD');
  };

  const schema = (onlyCNPJ
    ? AffiliateRegisterPersonalDataSchemaCNPJ(riqueredPhone)
    : AffiliateRegisterPersonalDataSchemaCPF(riqueredPhone)
  ).shape({
    ...(whitelabelCustomization.values &&
      role !== 'ADMIN' && {
        customFields: AffiliateRegisterPersonalDataSchemaWithCustomFields(
          whitelabelCustomization.requirements,
          whitelabelCustomization.values,
        ),
      }),
  });

  const getPageTitle = () => {
    if (MARY_KAY_WHITELABEL.includes(window.location.hostname)) {
      return 'Muito bem! Agora, confirme seus dados abaixo.';
    }

    return t('hubly-dashboard.signup.affiliate.right-side.second-step.title');
  };

  const pageTitle = getPageTitle();

  const disallowInitialValues =
    isFieldBlocked('email') && isFieldBlocked('name') && isFieldBlocked('document');

  return (
    <Flex flexDirection="column" sx={{ gap: '16px' }}>
      {!isMobile && <Spacer mt="48px" />}

      <TitleForm>{pageTitle}</TitleForm>

      {MARY_KAY_WHITELABEL.includes(window.location.hostname) && (
        <>
          <Flex
            alignItems="center"
            justifyContent="center"
            backgroundColor={'#faedef'}
            borderRadius="8px"
          >
            <span
              className="items-center"
              style={{ padding: '16px', fontSize: '14px', lineHeight: '18px', color: '#333333' }}
            >
              <span style={{ fontWeight: 500 }}>
                Para alterar os dados, atualize no Em Sintonia e entre em contato com a Central de
                Relacionamento.{' '}
              </span>{' '}
              <br />
              <br />
              <b>ATENÇÃO:</b> O e-mail é sua chave de cadastro nesta plataforma, caso você altere
              posteriormente lá no Em Sintonia, será enviando um novo convite de cadastro e você
              perderá o acesso da sua loja com e-mail anterior. <br />
              Para mais detalhes das regras clique e acesse o{' '}
              <a
                href="https://mk.marykayintouch.com.br/s/login/?language=pt_BR&ec=302&startURL=%2Fs%2Farticle%2FMary-Kay-a-Um-Clique-FAQ"
                target="_blank"
                style={{ color: '#d00070', fontWeight: 600 }}
                rel="noreferrer"
              >
                FAQ Mary Kay a Um Clique
              </a>
              .
            </span>
          </Flex>
        </>
      )}

      <Formik
        initialValues={{
          ...contextValues,
          name: contextValues.name ?? '',
          document: contextValues.document ?? '',
          phone: contextValues.phone ?? '',
        }}
        validationSchema={schema}
        validateOnMount={!disallowInitialValues}
        onSubmit={handleSubmitFormik}
      >
        {({
          initialValues,
          values,
          handleChange,
          handleSubmit,
          handleBlur,
          errors,
          status,
          isValid,
          touched,
          setTouched,
          setFieldError,
        }): JSX.Element => {
          return (
            <>
              <Input
                label={t(
                  'hubly-dashboard.signup.affiliate.right-side.second-step.form.full-name.label',
                )}
                placeholder={t(
                  'hubly-dashboard.signup.affiliate.right-side.second-step.form.full-name.placeholder',
                )}
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={e => {
                  handleBlur(e);
                  setTouched({
                    ...touched,
                    name: true,
                    phone: true,
                  });
                }}
                error={touched.name && !!errors.name}
                helperText={touched.name ? errors.name : ''}
                disabled={!!initialValues.name && isFieldBlocked('name')}
                fluid
              />

              <MaskedInput
                mask={
                  onlyCNPJ
                    ? '99.999.999/9999-99'
                    : getHublyFormUnmaskedValue(values.document).length > 11
                    ? '99.999.999/9999-99'
                    : '999.999.999-999'
                }
                type="tel"
                maskChar=""
                label={t(
                  onlyCNPJ
                    ? 'hubly-dashboard.signup.affiliate.right-side.second-step.form.document-cnpj.label'
                    : MARY_KAY_WHITELABEL.includes(window.location.hostname)
                    ? 'hubly-dashboard.signup.affiliate.right-side.second-step.form.document-cpf.label'
                    : 'hubly-dashboard.signup.affiliate.right-side.second-step.form.document-cpf-cnpj.label',
                )}
                placeholder={t(
                  onlyCNPJ
                    ? 'hubly-dashboard.signup.affiliate.right-side.second-step.form.document-cnpj.label'
                    : MARY_KAY_WHITELABEL.includes(window.location.hostname)
                    ? 'hubly-dashboard.signup.affiliate.right-side.second-step.form.document-cpf.label'
                    : 'hubly-dashboard.signup.affiliate.right-side.second-step.form.document-cpf-cnpj.label',
                )}
                name="document"
                value={values.document}
                onBlur={e => {
                  handleBlur(e);
                  setTouched({
                    ...touched,
                    document: true,
                    phone: true,
                  });
                }}
                onChange={handleChange}
                error={touched.document && !!errors.document}
                helperText={touched.document ? errors.document : ''}
                fluid
                disabled={!!initialValues.document && isFieldBlocked('document')}
              />

              <Input
                label={t(
                  'hubly-dashboard.signup.affiliate.right-side.second-step.form.email.label',
                )}
                placeholder={t(
                  'hubly-dashboard.signup.affiliate.right-side.second-step.form.email.placeholder',
                )}
                name="email"
                value={values.email}
                onBlur={async e => {
                  handleBlur(e);

                  if (!values.email || !OLIST_HOST_NAME.includes(window.location.hostname)) return;

                  try {
                    const { data } = await client.query({
                      query: gql`
                        query EmailExists($email: String!) {
                          emailExists(email: $email)
                        }
                      `,
                      variables: {
                        email: values.email,
                      },
                      fetchPolicy: 'network-only',
                    });

                    console.log({ data });

                    if (data.emailExists) {
                      setFieldError('email', 'Este e-mail já está cadastrado');
                    }
                  } catch (error) {
                    console.error(error);
                  }
                }}
                onChange={handleChange}
                error={touched.email && !!errors.email}
                helperText={touched.email ? errors.email : ''}
                disabled={!!initialValues.email && isFieldBlocked('email')}
                fluid
              />

              <MaskedInput
                mask="(99) 99999-9999"
                maskChar="_"
                type="tel"
                alwaysShowMask={false}
                label={
                  riqueredPhone
                    ? t(
                        'hubly-dashboard.signup.affiliate.right-side.second-step.form.required-phone.label',
                      )
                    : t('hubly-dashboard.signup.affiliate.right-side.second-step.form.phone.label')
                }
                placeholder={t(
                  'hubly-dashboard.signup.affiliate.right-side.second-step.form.phone.placeholder',
                )}
                name="phone"
                value={values.phone}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.phone && !!errors.phone}
                helperText={touched.phone ? errors.phone : ''}
                disabled={!!initialValues.phone && isFieldBlocked('phone')}
                fluid
                required={riqueredPhone}
              />

              {role !== 'ADMIN' && <AffiliateRegistrationCustomFields />}

              <Flex
                width="100%"
                marginTop={'20px'}
                flexDirection="column"
                style={{ gap: isMobile ? '12px' : '16px' }}
              >
                <Button
                  type="submit"
                  disabled={!isValid || status === 'validating'}
                  onClick={(): void => handleSubmit()}
                >
                  {t(
                    'hubly-dashboard.signup.affiliate.right-side.second-step.form.buttons.confirm.text',
                  )}
                </Button>

                <Button
                  hierarchy="tonalFilled"
                  onClick={handleBackToPasswordStep}
                  icon={'chevronLeft'}
                >
                  {t(
                    'hubly-dashboard.signup.affiliate.right-side.second-step.form.buttons.back.text',
                  )}
                </Button>
              </Flex>
            </>
          );
        }}
      </Formik>
    </Flex>
  );
};

export default AffiliateRegistrationPersonalDataStep;
